import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { merge, Observable, of, Subject, timer } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { IFinancingNegotiation, IFinancingNegotiationsFilters, IFinancingQuestion, IFinancingQuestionsFilters } from '../models/financing-negotiations.model';
import { Store } from '@ngxs/store';
import { AuthState } from '../states/auth.state';
import { IFinancingPublicQuestions } from '../models/financing-public-questions.model';
import { PageableModel } from '../models/pageable.model';
import { SHOW_LOADER } from '../core/interceptors/loader.interceptor';
import { PageableApiResult } from '../models/pagable-api-result.model';
import { and, comparison, eq, ge, le } from 'rsql-builder';
import { DateTime } from 'luxon';

@Injectable({
  providedIn: 'root'
})
export class NegotiationsApiService {

  constructor(private http: HttpClient, private store: Store) { }
  
  public startNewChat(body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/financhat/sincronizacion/negociacion`, body);
    // return this.http.post(`${environment.apiBaseUrl}/financhat/negociaciones`, body);
  }

  public getPublicQuestions(id: number): Observable<IFinancingPublicQuestions[]> {
    return this.http.get<IFinancingPublicQuestions[]>(`${environment.apiBaseUrl}/financhat/sincronizacion/solicitudes/${id}/preguntas`).pipe(
      map( res => {
        return res.map( question => {
          return {
            ...question,
            fechaPregunta: this.addZIfMissing(question.fechaPregunta),
            fechaRespuesta: this.addZIfMissing(question.fechaRespuesta),
          }
        })
      })
    )
  }

  public createPublicQuestion(idFinanciamiento: number, body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/financhat/sincronizacion/solicitudes/${idFinanciamiento}/preguntas`, body)
  }

  public replyPublicQuestion(idFinanciamiento: number, preguntaId: number, body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/financhat/sincronizacion/solicitudes/${idFinanciamiento}/preguntas/${preguntaId}/responder`, body)
  }

  public getMyNegotiations(filter?: IFinancingNegotiationsFilters): Observable<PageableModel<IFinancingNegotiation>> {
    const comps = [];
    let noFilter = true;

    if (filter?.estado) {
      comps.push(comparison('estado', eq(filter?.estado)));
      noFilter = false;
    }
    if (filter?.fechaDesde) {
      const fecha = filter?.fechaDesde instanceof DateTime ? filter?.fechaDesde.startOf('day').toJSDate().toISOString() : filter?.fechaDesde?.setUTCHours(0,0,0,0);
      comps.push(comparison('fechaCreacion', ge(fecha)));
      noFilter = false;
    }
    if (filter?.fechaHasta) {
      const fecha = filter?.fechaHasta instanceof DateTime ? filter?.fechaHasta.endOf('day').toJSDate().toISOString() : filter?.fechaHasta?.setUTCHours(23,59,59,99);
      comps.push(comparison('fechaCreacion', le(fecha)));
      noFilter = false;
    }
    if (filter?.cuitInversor) {
      comps.push(comparison('cuitInversor', eq(filter?.cuitInversor)));
      noFilter = false;
    }
    if (filter?.razonSocialInversor) {
      const razonSocialVal = filter?.razonSocialInversor.replace(/\s/g, '%');
      comps.push(`razonSocialInversor==*${razonSocialVal}*`);
      noFilter = false;
    }
    if (filter?.cuitPyme) {
      comps.push(comparison('cuitEmpresaPyme', eq(filter?.cuitPyme)));
      noFilter = false;
    }
    if (filter?.razonSocialPyme) {
      const razonSocialPymeVal = filter?.razonSocialPyme.replace(/\s/g, '%');
      comps.push(`razonSocialPyme==*${razonSocialPymeVal}*`);
      noFilter = false;
    }
    if (filter?.montoMinimo) {
      comps.push(comparison('montoSolicitado', ge(filter?.montoMinimo)));
      noFilter = false;
    }
    if (filter?.montoMaximo) {
      comps.push(comparison('montoSolicitado', le(filter?.montoMaximo)));
      noFilter = false;
    }
    if (filter?.ofertaActivas != null) {
      comps.push(comparison('ofertaActivas', eq(filter?.ofertaActivas)));
      noFilter = false;
    }
    
    let params = new HttpParams();
    
    
    if (comps.length > 0) {
      let q = and(...comps).toString();
      params = params.set('rquery', q);
    }

    if (filter?.perfil) {
      params = params.append('perfil', filter?.perfil);
    }

    if (filter?.sortByfechaCreacionMensaje) {
      params = params
        .append('rsort', 'fechaCreacionMensaje') 
        .append('rdirection', filter?.sortByfechaCreacionMensaje);
    }

    if (filter?.page) {
      params = params.append('page', filter?.page.toString());
    }
    if (filter?.size) {
      params = params.append('size', filter?.size.toString());
    }

    return this.http.get<PageableModel<IFinancingNegotiation>>(`${environment.apiBaseUrl}/financhat/negociaciones/mis-negociaciones`, { params });
  }

  public getMyInvestorNegotiations(filter?: IFinancingNegotiationsFilters): Observable<IFinancingNegotiation[]> {
    const userId = this.store.selectSnapshot(AuthState.getUserInfo).data.id;
    return this.http.get<IFinancingNegotiation[]>(`${environment.apiBaseUrl}/financhat/negociaciones/mis-negociaciones`).pipe(
      map( res => res.filter(n => n.inversor.id == Number(userId)))
    );
  }
  
  public getChatById(id: number, spinner: boolean = true): Observable<IFinancingNegotiation> {
    return this.http.get<IFinancingNegotiation>(`${environment.apiBaseUrl}/financhat/negociaciones/${id}`,
      { context: spinner ? undefined : new HttpContext().set(SHOW_LOADER, false) }
    ).pipe(
      map(neg => this.normalizeNegotiationDates(neg))
    );
  }

  public markChatAsRead(id: number): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/financhat/negociaciones/${id}/unread?unread=false`, {})
  }

  public sendNewMessage(id: number, body: FormData): Observable<IFinancingNegotiation> {
    console.log('body', body)
    return this.http.post(`${environment.apiBaseUrl}/financhat/negociaciones/${id}/mensajes`, body).pipe(
      map(neg => this.normalizeNegotiationDates(neg))
    );
  }

  public downloadMessageFile(msgId: number, fileId: number): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/financhat/mensajes/${msgId}/files/${fileId}/download`, { responseType: 'blob'})
  }

  private normalizeNegotiationDates(neg: any): IFinancingNegotiation {
  return {
    ...neg,
    fechaCreacion: this.addZIfMissing(neg.fechaCreacion),
    fechaActualizacion: this.addZIfMissing(neg.fechaActualizacion),
    mensajes: neg.mensajes?.map(m => ({
      ...m,
      fechaCreacion: this.addZIfMissing(m.fechaCreacion),
    })) ?? []
  };
}

private addZIfMissing(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;

  // avoid double Z or offset
  if (dateStr.endsWith('Z') || /[+-]\d\d:\d\d$/.test(dateStr)) {
    return dateStr;
  }

  return dateStr + 'Z';
}

  public closeChatById(id: number): Observable<any> {
    return this.http.patch(`${environment.apiBaseUrl}/financhat/negociaciones/${id}/cerrar`, {});
  }

  public requestOpenChatById(id: number): Observable<any> {
    return this.http.patch(`${environment.apiBaseUrl}/financhat/negociaciones/${id}/reapertura`, {});
  }

  public confirmReopenChatById(id: number): Observable<any> {
    return this.http.patch(`${environment.apiBaseUrl}/financhat/negociaciones/${id}/confirm-reapertura`, {});
  }

  public getAllMyQuestions(filter: IFinancingQuestionsFilters): Observable<PageableApiResult<IFinancingQuestion>> {
    return this.http.get<PageableApiResult<IFinancingQuestion>>(`${environment.apiBaseUrl}/financhat/preguntas/my?page=${filter.page}&size=${filter.size}`)
  }

  public markQuestionAsRead(id: number, leida: boolean): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/financhat/preguntas/${id}/lectura?leida=${leida}`, {})
  }

  public markAllQuestionsAsRead(): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/financhat/preguntas/lectura/todas`, {}, { context: new HttpContext().set(SHOW_LOADER, false) })
  }

  private getHasUnreadMessages(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/financhat/usuarios/pendientes/my`, { context: new HttpContext().set(SHOW_LOADER, false) });
  }

  // Además del poll, se vuelve a consultar a pedido: si no, después de marcar una
  // pregunta como leída el indicador del menú tarda hasta 30s en apagarse.
  private refreshUnreadInfo$ = new Subject<void>();

  private hasUnreadInfoSource$ = merge(timer(0, 30000), this.refreshUnreadInfo$).pipe(
      switchMap(() => this.getHasUnreadMessages()),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      catchError((error, caught) => {
        console.error('Error obteniendo actualización', error);
        return of(null);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  hasUnreadMessages$ = this.hasUnreadInfoSource$;

  public refreshUnreadInfo(): void {
    this.refreshUnreadInfo$.next();
  }
}
